import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styles from './page.module.css';
import AppBar from '../../../components/AppBar';
import { useTheme } from '../../../context/ThemeProvider';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import ContentGrid from '../../../components/ContentGrid';
import { GlobalMicroButton } from '../../../components/GlobalMicroButton';
import GlobalTabs from '../../../components/GlobalTabs';
import AppFooter from '../../../components/AppFooter';
import { UserTile } from '../../../components/UserTile';
import { CompanyCard } from '../../../components/CompanyCard';
import { FilterBar } from '../../../components/FilterBar';
import GlobalEmptyState from '../../../components/GlobalEmptyState';
import InvitationModal from '../../../components/InvitationModal';
import { CompanySettings } from '../../../components/CompanySettings';
import { GlobalPagination } from '../../../components/GlobalPagination';
import { ProjectList } from '../../../components/ProjectTile';
import { ProjectModal } from '../../../components/ProjectModal';
import { KnowledgeDocumentsTab } from '../../../components/KnowledgeDocumentsTab';
import AddKnowledgeDocumentSidebar from '../../../components/AddKnowledgeDocumentSidebar';
import { useProjects } from '../../../hooks/useProjects';
import { useKnowledgeDocuments } from '../../../hooks/useKnowledgeDocuments';
import DoubleConfirmationModal from '../../../components/DoubleConfirmationModal';
import { useToast } from '../../../context/ToastProvider';
import { useUser } from '../../../context/UserContext';
import GlobalGptAccessPanel from '../../../components/GlobalGptAccessPanel';
import { Company, FamilyMember } from './types';
import {
  getCompanyById,
  getCompanyUsers,
  getCompanyDocuments,
  deleteCompany,
  buildPagination as buildPaginationController,
  paginate as paginateController,
  filterDocuments as filterDocumentsController,
  filterUsers as filterUsersController,
  buildInvoiceStatusOptions,
  buildCategoryOptions,
  buildUserStatusOptions,
  extractSponsorshipBadge,
} from './controller';

export default function CompanyPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const showToast = useToast();
  const { role } = useUser();
  const [targetCompany, setTargetCompany] = useState<Company | null>(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [companyUsers, setCompanyUsers] = useState<FamilyMember[]>([]);
  const [companyDocuments, setCompanyDocuments] = useState<any[]>([]);
  const [, setSponsorshipBadge] = useState<{enabled: boolean; text: string; logoUrl: string} | null>(null);
  
  // Filter states for invoices
  const [invoiceSearchQuery] = useState('');
  const [invoiceStatusFilter] = useState('');
  const [invoiceCategoryFilter] = useState('');
  
  // Filter states for users
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userRoleFilter] = useState('');
  
  // Modal states
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAddKnowledgeDocumentSidebar, setShowAddKnowledgeDocumentSidebar] = useState(false);
  const [, setIsMobileView] = useState<boolean>(false);
  const ITEMS_PER_PAGE = 12;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invoicePage, setInvoicePage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);

  // Projects state
  const { projects, loading: projectsLoading, refetch: fetchProjects } = useProjects();
  const [projectSearchQuery, setProjectSearchQuery] = useState('');

  // Knowledge documents for this company (knowledge/{companyId}/documents)
  const { documents: companyKnowledgeDocuments } = useKnowledgeDocuments(companyId);

  // Filter options (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const statusOptions = buildInvoiceStatusOptions(t);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const categoryOptions = buildCategoryOptions(t);
  const userStatusOptions = buildUserStatusOptions(t);

  // Projects available for this company - filter by companyId OR availableProjects
  const companyProjects = projects.filter(project => {
    // Include if project belongs to this company via companyId
    if (project.companyId === companyId) return true;
    // Also include if project is in the availableProjects array
    if (targetCompany?.availableProjects && targetCompany.availableProjects.includes(project.id)) return true;
    return false;
  });

  // Filter documents based on search and filters (computed but not currently displayed)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filteredDocuments = filterDocumentsController(companyDocuments, {
    search: invoiceSearchQuery,
    status: invoiceStatusFilter,
    category: invoiceCategoryFilter,
  });

  // Filter users based on search and filters
  const filteredUsers = filterUsersController(companyUsers, {
    search: userSearchQuery,
    status: userStatusFilter,
    role: userRoleFilter,
  });

  // Pagination helpers
  const buildPagination = (totalItems: number, currentPage: number) =>
    buildPaginationController(totalItems, currentPage, ITEMS_PER_PAGE);

  // Paginate items
  const paginate = <T,>(items: T[], page: number) =>
    paginateController<T>(items, page, ITEMS_PER_PAGE);

  // Fetch users associated with the company
  const fetchCompanyUsers = useCallback(async (companyId: string) => {
    try {
      const users = await getCompanyUsers(companyId, t);
      setCompanyUsers(users);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching company users:', error);
      }
      setCompanyUsers([]);
    }
  }, [t]);

  // Fetch documents associated with the company
  const fetchCompanyDocuments = useCallback(async (companyId: string) => {
    try {
      const documents = await getCompanyDocuments(companyId, t);
      setCompanyDocuments(documents);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching company documents:', error);
      }
      setCompanyDocuments([]);
    }
  }, [t]);

  // Fetch target company data
  const fetchTargetCompany = useCallback(async () => {
    if (!companyId) return;

    setLoadingCompany(true);
    try {
      const company = await getCompanyById(companyId, t);
      if (!company) {
        navigate('/home');
        return;
      }
      setTargetCompany(company);
      const badge = extractSponsorshipBadge(company);
      if (badge) setSponsorshipBadge(badge);

      await Promise.all([
        fetchCompanyUsers(company.id),
        fetchCompanyDocuments(company.id)
      ]);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching target company:', error);
      }
      navigate('/home');
    } finally {
      setLoadingCompany(false);
    }
  }, [companyId, navigate, t, fetchCompanyUsers, fetchCompanyDocuments]);

  // Refresh company record for the header card / tabs without full-page loading
  const refreshTargetCompany = useCallback(async () => {
    if (!companyId) return;
    try {
      const company = await getCompanyById(companyId, t);
      if (!company) return;
      setTargetCompany(company);
      const badge = extractSponsorshipBadge(company);
      if (badge) setSponsorshipBadge(badge);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error refreshing company:', error);
      }
    }
  }, [companyId, t]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchTargetCompany();
  }, [fetchTargetCompany]);

  // Handle viewport width changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handler functions
  const handleAddUser = () => {
    setShowInvitationModal(true);
  };

  const handleUserAdded = () => {
    // Refresh the company users list
    if (companyId) {
      fetchCompanyUsers(companyId);
    }
  };

  // Delete company
  const handleDeleteCompany = useCallback(async () => {
    if (!companyId) return;
    setIsDeletingCompany(true);
    try {
      await deleteCompany(companyId);
      showToast(t('company_page_delete_success') || 'Company deleted', 'success');
      navigate('/home');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Company delete failed:', error);
      }
      showToast(t('company_page_delete_error') || 'Failed to delete company', 'error');
    } finally {
      setIsDeletingCompany(false);
      setShowDeleteModal(false);
    }
  }, [companyId, navigate, showToast, t]);


  // Loading state
  if (loadingCompany) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%'
      }}>
        <img
          src={isDarkMode ? "/assets/graphics/thescon-loading-white.svg" : "/assets/graphics/thescon-loading.svg"}
          alt="Loading"
          width={120}
          height={120}
        />
      </div>
    );
  }

  // Not found state
  if (!targetCompany) {
    return (
      <div className={styles.container}>
        <AppBar />
        <ContentGrid rows='64px 180px 1fr 48px'>
          <div className={styles.loadingContainer}>
            <p>{t('company_page_not_found')}</p>
          </div>
        </ContentGrid>
      </div>
    );
  }

  return (
    <div>
      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid rows='64px 136px 1fr 48px'>

        {/* Toolbar left side */}
        <div className={styles.homeToolbarLeft}>
          
          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('company_page_navigate_back')}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => navigate('/home?tab=companies')}
            />
          </div>

          <b>{t('company_page_company_details')}</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>
          <GlobalMicroButton
            title={t('global_delete')}
            icon={<img src="/assets/icons/trash.red.svg" alt="Delete" width={16} height={16} />}
            onClick={() => setShowDeleteModal(true)}
            destructive
          >
            {t('global_delete')}
          </GlobalMicroButton>
        </div>

        {/* Company card */}
        <div className={styles.companyProfileWrapper}>
          <CompanyCard
            name={targetCompany.name}
            address_zip_code={targetCompany.address_zip_code}
            address_city={targetCompany.address_city}
            address_country={targetCompany.address_country}
            website={targetCompany.website}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Tabs */}
        <div className={styles.homeTabs}>
          {(() => {
            const searchParams = new URLSearchParams(location.search);
            const tabParam = searchParams.get('tab') || '';
            // Build tab keys mirroring the rendered order
            const tabKeys = [
              'projects',
              'knowledge',
              'users',
              'settings',
            ];
            const initialTabIndex = Math.max(0, tabKeys.indexOf(tabParam));
            const handleTabChange = (index: number) => {
              const params = new URLSearchParams(location.search);
              params.set('tab', tabKeys[index] || '');
              navigate({ search: params.toString() }, { replace: true });
            };
            return (
          <GlobalTabs
            key={tabParam || 'default'}
            initialIndex={initialTabIndex}
            onChange={handleTabChange}
            tabs={[
              {
                label: `${t('home_tab_projects')} (${companyProjects.length})`,
                content: (
                  <div>
                    <ProjectList
                      projects={companyProjects}
                      companies={targetCompany ? [{ id: targetCompany.id, website: targetCompany.website }] : []}
                      loading={projectsLoading}
                      searchValue={projectSearchQuery}
                      onSearchChange={setProjectSearchQuery}
                      onAddProject={() => setShowProjectModal(true)}
                    />
                  </div>
                ),
              },
              {
                label: `${t('home_tab_knowledge')} (${companyKnowledgeDocuments.length})`,
                content: companyId ? (
                  <KnowledgeDocumentsTab
                    knowledgeId={companyId}
                    onAddDocument={() => setShowAddKnowledgeDocumentSidebar(true)}
                  />
                ) : (
                  <div className={styles.loadingContainer}><p>{t('company_page_loading')}</p></div>
                ),
              },
              {
                label: `${t('company_page_users_tab')} (${filteredUsers.length})`,
                content: (
                  <div className={styles.userList}>
                    <FilterBar
                      filters={[
                        {
                          id: 'status',
                          label: t('company_page_status_filter'),
                          options: userStatusOptions,
                          value: userStatusFilter,
                          onChange: setUserStatusFilter
                        }
                      ]}
                      searchValue={userSearchQuery}
                      onSearchChange={setUserSearchQuery}
                      searchPlaceholder={t('company_page_search_users_placeholder')}
                      showInviteButton={true}
                      onAddUser={handleAddUser}
                    />

                    <div style={{ marginTop: filteredUsers.length > 0 ? '16px' : '0' }}>
                      {filteredUsers.length === 0 ? (
                        <GlobalEmptyState
                          buttonText={t('company_page_add_first_user')}
                          onButtonClick={handleAddUser}
                          buttonIcon={
                            <img 
                              src={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"} 
                              alt="Add" 
                              width={12} 
                              height={12} 
                            />
                          }
                        />
                      ) : (
                        <>
                          <div className={styles.usersContainer}>
                            {paginate(filteredUsers, usersPage).map((user) => (
                              <div key={user.id} className={styles.userItem}>
                                <UserTile user={user} />
                              </div>
                            ))}
                          </div>
                          <div style={{ marginTop: '12px' }}>
                            <GlobalPagination
                              pagination={buildPagination(filteredUsers.length, usersPage)}
                              actions={{
                                goToPage: setUsersPage,
                                nextPage: () => setUsersPage(p => p + 1),
                                previousPage: () => setUsersPage(p => p - 1),
                                firstPage: () => setUsersPage(1),
                                lastPage: () => setUsersPage(Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE)))
                              }}
                              showItemsPerPage={false}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ),
              },
              {
                label: t('company_settings_tab'),
                content: (
                  <div className={styles.settingsTab}>
                    <CompanySettings
                      companyId={companyId || ''}
                      onCompanyUpdated={refreshTargetCompany}
                    />
                  </div>
                ),
              },
            ]}
          />
            );
          })()}
        </div>

        {/* Separator */}
        <div className={styles.gridSeparatorWrapper}>
          <div className={styles.gridSeparator} />
        </div>

        {/* Side section - Archive Chat */}
        {role === 'internal_admin' && (
          <div className={styles.sideSection}>
            <GlobalGptAccessPanel companyId={companyId} />
          </div>
        )}

        {/* Footer */}
        <AppFooter inline />

      </ContentGrid>

      {/* Invitation modal */}
      <InvitationModal
        isOpen={showInvitationModal}
        onClose={() => setShowInvitationModal(false)}
        userId={companyId || ''}
        existingUserData={{
          name: '',
          email: '',
          role: ''
        }}
        onUserAdded={handleUserAdded}
        showRoleSelection={true}
        preselectedCompanyId={companyId}
      />
      
      {/* Project modal */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onProjectAdded={() => {
          fetchProjects();
          setProjectSearchQuery('');
        }}
        preselectedCompanyId={companyId || undefined}
      />

      {/* Add knowledge document sidebar */}
      <AddKnowledgeDocumentSidebar
        isOpen={showAddKnowledgeDocumentSidebar}
        onClose={() => setShowAddKnowledgeDocumentSidebar(false)}
        knowledgeId={companyId || ''}
        companyId={companyId || ''}
        onSuccess={() => setShowAddKnowledgeDocumentSidebar(false)}
      />

      {/* Delete confirmation modal */}
      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={`Delete "${targetCompany?.name || t('company_page_unknown_company')}"?`}
        paragraph={t('home_document_list_delete_document_confirmation_text')}
        primaryLabel={t('global_delete')}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('document_page_cancel')}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDeleteCompany}
        onSecondary={() => setShowDeleteModal(false)}
        primaryDestructive
        loading={isDeletingCompany}
      />
    </div>
  );
}
